import { Link, useParams } from "react-router-dom";
import { api, useApi } from "../api";
import { PageShell } from "../components/Layout";
import { ChapterTag, FaqAccordion, Marquee } from "../components/Motion";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { LinkPills, Loading, PageHero, SectionBlock } from "../components/Sections";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data, loading, error } = useApi(() => api.service(slug), [slug]);

  if (loading) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (error || !data) {
    return (
      <PageShell>
        <NotFoundBlock />
      </PageShell>
    );
  }

  const origin = window.location.origin;
  const s = data.service;

  return (
    <PageShell>
      <Seo title={data.title} description={data.description} path={`/services/${slug}`} schema={faqSchema(data.faqs)} />
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(data.breadcrumb, origin))}</script>

      <PageHero eyebrow={data.eyebrow} lines={data.h1_lines} intro={data.intro} breadcrumb={data.breadcrumb} />
      <Marquee items={s.keywords.map((k) => k.replace(/\b\w/g, (c) => c.toUpperCase()))} />

      {data.sections.map((sec, i) => (
        <SectionBlock key={i} index={i} heading={sec.heading} paragraphs={sec.paragraphs} bullets={sec.bullets}>
          {i === 1 &&
            data.cities_by_region.map((g) => (
              <div key={g.region} className="mt-8">
                <h3 className="font-display font-bold uppercase tracking-widest text-amber text-base mb-3">
                  {s.name} in {g.region}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {g.cities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/locations/${c.slug}/${slug}`}
                      data-testid={`service-city-link-${c.slug}`}
                      className="font-mono text-[11px] uppercase tracking-wider px-3 py-2 border border-white/10 bg-card text-gray-300 hover:border-amber hover:text-amber transition-colors duration-300"
                    >
                      {s.name} {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
        </SectionBlock>
      ))}

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14" data-testid="service-faq-section">
        <ChapterTag num="FAQ" title={`${s.name} — QUESTIONS`} />
        <FaqAccordion faqs={data.faqs} testIdPrefix="service-faq" />
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <ChapterTag num="NEXT" title="RELATED SERVICES" />
        <LinkPills items={data.related} prefix="/services" testIdPrefix="related-service" />
      </section>
    </PageShell>
  );
}

function NotFoundBlock() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4" data-testid="service-not-found">
      <p className="font-display font-black uppercase text-4xl text-white">Service not found</p>
      <Link to="/services" className="font-mono text-xs uppercase tracking-widest text-amber border border-amber px-5 py-3">
        View all services
      </Link>
    </div>
  );
}
